"use client";

import React, { useEffect, useRef, useState } from "react";
import { Bitcoin, HeartCrack, ShieldAlert, Activity, Clock, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const threatCategories = [{
  id: "wallet",
  title: "Wallet Access Recovery",
  description: "Non-custodial recovery using heuristic analysis and brute-force decryption for lost private keys or corrupted wallet files.",
  icon: Bitcoin,
  successRate: 89,
  avgTime: "3-7 Days",
  actionText: "Consult Expert"
}, {
  id: "investigation",
  title: "Scam & Fraud Analysis",
  description: "Deep-layer transaction graphing to de-anonymize illicit actors and map the flow of stolen funds through mixers and exchanges.",
  icon: ShieldAlert,
  successRate: 94,
  avgTime: "5-14 Days",
  actionText: "Start Investigation"
}, {
  id: "forensics",
  title: "Enterprise Asset Tracing",
  description: "Real-time ledger monitoring and AML-compliant forensic reporting for institutional-grade asset reclamation and legal support.",
  icon: Activity,
  successRate: 97,
  avgTime: "10-21 Days",
  actionText: "Request Tracing"
}];
export default function SpecializedThreatMatrix() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="162885" id="our-technical-expertise" className="dark bg-background text-foreground relative py-24 overflow-hidden">
            {}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[150px]" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#00ffff08_1px,transparent_1px),linear-gradient(to_bottom,#00ffff08_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
            </div>

            {}
            <style dangerouslySetInnerHTML={{
      __html: `
                @keyframes draw-svg {
                    0% { stroke-dasharray: 1000; stroke-dashoffset: 1000; opacity: 0; }
                    10% { opacity: 1; }
                    100% { stroke-dasharray: 1000; stroke-dashoffset: 0; opacity: 1; }
                }
                .animate-svg-draw path, 
                .animate-svg-draw circle, 
                .animate-svg-draw rect, 
                .animate-svg-draw line, 
                .animate-svg-draw polyline, 
                .animate-svg-draw polygon {
                    animation: draw-svg 2.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                }
            `
    }} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-sm font-mono tracking-widest text-primary uppercase mb-3 flex items-center justify-center gap-2">
                        <Activity className="size-4" />
                        Forensic Methodology
                    </h2>
                    <p className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
                        Our Technical Expertise
                    </p>
                    <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                        Leveraging proprietary heuristic analysis and transaction graphing to navigate complex blockchain architecture and secure digital assets.
                    </p>
                </div>

                <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {threatCategories.map((category, i) => {
          const Icon = category.icon;
          return <div key={category.id} data-index={i} className={`
                                    group relative rounded-lg border border-primary/10 bg-card/40 backdrop-blur-xl 
                                    p-8 transition-all duration-500 ease-out
                                    hover:border-primary/40 hover:shadow-[0_0_30px_rgba(0,255,255,0.08)] 
                                    hover:-translate-y-1 overflow-hidden
                                    ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                                `} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                                {}
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="relative z-10 flex flex-col h-full">
                                    <div className="mb-6 inline-flex items-center justify-center rounded-sm bg-primary/10 p-4 ring-1 ring-inset ring-primary/20 group-hover:bg-primary/20 transition-colors duration-300">
                                        <Icon className={`size-8 text-primary ${isVisible ? 'animate-svg-draw' : 'opacity-0'}`} strokeWidth={1.5} />
                                    </div>
                                    
                                    <h3 className="text-2xl font-semibold text-foreground mb-3 font-sans tracking-tight">
                                        {category.title}
                                    </h3>
                                    
                                    <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                                        {category.description}
                                    </p>

                                    {}
                                    <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
                                        <div className="overflow-hidden">
                                            <div className="pt-6 mt-2 border-t border-border/50 flex flex-col gap-5 transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100">
                                                
                                                {}
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-muted-foreground font-medium font-sans">Success Rate</span>
                                                        <span className="text-primary font-mono font-bold">{category.successRate}%</span>
                                                    </div>
                                                    <Progress value={category.successRate} className="h-1 bg-primary/10" style={{
                        '--progress-background': 'var(--primary)'
                      } as React.CSSProperties} />
                                                </div>

                                                {}
                                                <div className="flex justify-between items-center text-sm bg-muted/20 p-3 rounded-sm border border-border/30">
                                                    <div className="flex items-center gap-2 text-muted-foreground font-sans">
                                                        <Clock className="size-4" />
                                                        <span>Avg. Recovery</span>
                                                    </div>
                                                    <span className="text-foreground font-mono font-medium">{category.avgTime}</span>
                                                </div>

                                                {}
                                                <Button asChild variant="outline" className="w-full mt-2 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300 group/btn rounded-sm">
                                                    <Link to="/ContactPage">
                                                        {category.actionText}
                                                        <ArrowRight className="size-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                                                    </Link>
                                                </Button>
                                                
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>;
        })}
                </div>
            </div>
        </section>;
}